import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
} from "react-bootstrap";
import {
  useGetJobByIdQuery,
  useSubmitApplicationMutation,
} from "../../../redux/api/CareersApi/careersApi";
import {
  primaryColor,
  secondaryColor,
} from "../../../Constants/Colors";
import { message } from "antd";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { FaPaperPlane, FaFileUpload, FaArrowLeft } from "react-icons/fa";

const ApplyJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, isLoading: isJobLoading } = useGetJobByIdQuery(id);
  const [submitApplication, { isLoading: isSubmitting }] =
    useSubmitApplicationMutation();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    totalExperience: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    portfolioUrl: "",
    linkedinUrl: "",
    coverLetter: "",
  });

  const [resume, setResume] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setResume(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      message.error("Please upload your resume");
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append("resume", resume);
    data.append("job_id", id);

    try {
      await submitApplication(data).unwrap();
      message.success("Application submitted successfully!");
      navigate("/careers");
    } catch (error) {
      message.error(error.data?.error || "Failed to submit application");
    }
  };

  if (isJobLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="application-portal-bg min-vh-100 py-5">
      <Container>
        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            {/* Header Section */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-4">
              <div className="d-flex align-items-center gap-4">
                <Button
                  variant="white"
                  onClick={() => navigate(-1)}
                  className="back-btn shadow-sm rounded-circle d-flex align-items-center justify-content-center border-0"
                >
                  <FaArrowLeft size={18} style={{ color: primaryColor }} />
                </Button>
                <div>
                  <h2 className="fw-bold mb-1 text-dark">
                    Apply for {job?.title}
                  </h2>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="accent-dot"
                      style={{ backgroundColor: secondaryColor }}
                    ></span>
                    <p className="text-secondary mb-0 small fw-medium text-uppercase ls-1">
                      {job?.category} • {job?.location || "Remote"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Application Form (Full Page Layout) */}
            <div className="application-form-wrapper">
              <Form onSubmit={handleSubmit} className="custom-form">
                {/* Section 1: Identity */}
                <div className="form-section mb-5">
                  <div className="section-header d-flex align-items-center gap-3 mb-4">
                    <div
                      className="section-icon shadow-sm"
                      style={{
                        backgroundColor: primaryColor + "15",
                        color: primaryColor,
                      }}
                    >
                      1
                    </div>
                    <h5 className="fw-bold mb-0">Personal Identity</h5>
                  </div>
                  <Row className="g-4">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Full Name *
                        </Form.Label>
                        <Form.Control
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                          placeholder="John Doe"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Email Address *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                          placeholder="john@example.com"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Phone Number *
                        </Form.Label>
                        <Form.Control
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                          placeholder="+91 XXXXX XXXXX"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Section 2: Career Path */}
                <div className="form-section mb-5">
                  <div className="section-header d-flex align-items-center gap-3 mb-4">
                    <div
                      className="section-icon shadow-sm"
                      style={{
                        backgroundColor: primaryColor + "15",
                        color: primaryColor,
                      }}
                    >
                      2
                    </div>
                    <h5 className="fw-bold mb-0">Professional Summary</h5>
                  </div>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Total Experience *
                        </Form.Label>
                        <Form.Control
                          name="totalExperience"
                          value={formData.totalExperience}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                          placeholder="e.g. 5 Years"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Notice Period *
                        </Form.Label>
                        <Form.Control
                          name="noticePeriod"
                          value={formData.noticePeriod}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          required
                          placeholder="e.g. Immediate"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Current CTC
                        </Form.Label>
                        <Form.Control
                          name="currentCTC"
                          value={formData.currentCTC}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="e.g. 10 LPA"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Expected CTC
                        </Form.Label>
                        <Form.Control
                          name="expectedCTC"
                          value={formData.expectedCTC}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="e.g. 12 LPA"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Section 3: Portfolio & Attachments */}
                <div className="form-section mb-5">
                  <div className="section-header d-flex align-items-center gap-3 mb-4">
                    <div
                      className="section-icon shadow-sm"
                      style={{
                        backgroundColor: primaryColor + "15",
                        color: primaryColor,
                      }}
                    >
                      3
                    </div>
                    <h5 className="fw-bold mb-0">Portfolio & Attachments</h5>
                  </div>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          LinkedIn URL
                        </Form.Label>
                        <Form.Control
                          name="linkedinUrl"
                          value={formData.linkedinUrl}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="linkedin.com/in/username"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Portfolio / Web
                        </Form.Label>
                        <Form.Control
                          name="portfolioUrl"
                          value={formData.portfolioUrl}
                          onChange={handleChange}
                          disabled={isSubmitting}
                          placeholder="yourportfolio.com"
                          className="clever-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <div className="mb-2">
                        <Form.Label className="small fw-bold text-muted text-uppercase">
                          Resume / CV *
                        </Form.Label>
                      </div>
                      <div
                        className={`clever-upload-zone p-4 text-center transition-all ${isSubmitting ? "opacity-50" : ""}`}
                        style={{
                          pointerEvents: isSubmitting ? "none" : "auto",
                        }}
                      >
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="file-input-hidden"
                          accept=".pdf,.doc,.docx"
                          id="resumeUpload"
                          disabled={isSubmitting}
                        />
                        <label
                          htmlFor="resumeUpload"
                          className="w-100 cursor-pointer"
                        >
                          <div
                            className="upload-icon-wrapper mb-2 mx-auto"
                            style={{ backgroundColor: primaryColor + "10" }}
                          >
                            <FaFileUpload
                              size={24}
                              style={{ color: primaryColor }}
                            />
                          </div>
                          <p className="mb-1 fw-bold text-dark text-center">
                            {resume ? resume.name : "Select your Resume"}
                          </p>
                          <span className="text-muted small d-block text-center">
                            Supports PDF, DOC, DOCX (Max 5MB)
                          </span>
                        </label>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Section 4: Final Touch */}
                <div className="form-section mb-5">
                  <Form.Group>
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-0">
                        Additional Insights
                      </Form.Label>
                      <span className="small text-muted fw-normal">
                        (Optional)
                      </span>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="Tell us about a project you're proud of or why you'd like to join our team."
                      className="clever-input py-3"
                      style={{ resize: "none" }}
                    />
                  </Form.Group>
                </div>

                {/* Footer Actions */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-5 pt-4 border-top gap-4">
                  <div className="submission-hint d-flex align-items-center gap-2">
                    <div
                      className="pulse-dot"
                      style={{ backgroundColor: secondaryColor }}
                    ></div>
                    <span className="small text-muted">
                      A recruiter will review your application within 48 hours.
                    </span>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="clever-submit-btn px-5 py-3 rounded-pill fw-bold border-0 shadow-sm d-flex align-items-center gap-3 text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? (
                      <>
                        <LoaderSpinner
                          height="24"
                          width="24"
                          wrapperClassName="d-inline-flex"
                        />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Finalize Application</span>
                        <FaPaperPlane size={14} />
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>

      <style>{`
        .application-portal-bg {
          background: radial-gradient(circle at top right, ${primaryColor}08, transparent),
                      radial-gradient(circle at bottom left, ${primaryColor}05, transparent),
                      #fcfdfe;
        }
        
        .accent-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        
        .back-btn {
          width: 45px;
          height: 45px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .back-btn:hover {
          transform: translateX(-5px);
          background-color: ${primaryColor}08 !important;
        }
        
        .status-badge {
          background-color: #e6f7ff;
          color: ${primaryColor};
          font-weight: 600;
          font-size: 13px;
          border: 1px solid ${primaryColor}20;
        }
        
        .application-card {
          position: relative;
          border: 1px solid rgba(0,0,0,0.03) !important;
        }
        
        .clever-accent-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          z-index: 10;
        }
        
        .section-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
        }
        
        .clever-input {
          border: 1.5px solid #edf2f7 !important;
          border-radius: 12px !important;
          padding: 12px 18px !important;
          font-size: 15px !important;
          transition: all 0.2s ease !important;
          background-color: #fbfcfd !important;
        }
        
        .clever-input:focus {
          border-color: ${primaryColor} !important;
          background-color: #fff !important;
          box-shadow: 0 0 0 4px ${primaryColor}10 !important;
          outline: none !important;
        }
        
        .clever-upload-zone {
          border: 2px dashed #edf2f7;
          border-radius: 16px;
          cursor: pointer;
          position: relative;
          background-color: #fbfcfd;
        }
        
        .clever-upload-zone:hover {
          border-color: ${primaryColor};
          background-color: ${primaryColor}05;
        }
        
        .upload-icon-wrapper {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .file-input-hidden {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          opacity: 0;
          cursor: pointer;
        }
        
        .clever-submit-btn {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-size: 15px;
        }
        
        .clever-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px ${primaryColor}30 !important;
          opacity: 0.95;
        }
        
        .pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 ${secondaryColor}60; }
          70% { box-shadow: 0 0 0 10px ${secondaryColor}00; }
          100% { box-shadow: 0 0 0 0 ${secondaryColor}00; }
        }
        
        .ls-1 { letter-spacing: 0.5px; }
        
        @media (max-width: 768px) {
          .clever-submit-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default ApplyJob;
